/**
 * Channel data translation layer — v.1-D §7.8 + §10.
 *
 * The Convex backend speaks one vocabulary (`running`, `ad_break`, …); the
 * mockup JSX speaks another (`live`, `adbreak`, …). Every translation lives
 * in this file so components never inline a status mapping (R-22 / V.23).
 *
 * Exports:
 *   - `toMockupStatus(backendStatus)`        — §10.1 status translation
 *   - `toDestinationsArray(dest)`            — §10.2 destinations translation
 *   - `alertEmailRecipientsAdapter(rawValue)` — DISCREPANCY-2 microfix
 *
 * The `ChannelState` type mirrors §6.1 C.1 verbatim. It is duplicated here
 * intentionally so this module compiles independently of
 * `src/lib/state-machines/channelState.ts` (authored in T305). When T305 lands,
 * the canonical type lives there; this local alias remains the
 * narrowing-resilient ground truth for the adapter's `switch` exhaustiveness.
 */

/** Backend ChannelState (v.1-D §6.1 C.1). */
export type ChannelState =
  | 'idle'
  | 'starting'
  | 'running'
  | 'ad_break'
  | 'stopping'
  | 'error';

/** Mockup ChannelStatus (v.1-D §10.1). */
export type MockupChannelStatus =
  | 'idle'
  | 'starting'
  | 'live'
  | 'adbreak'
  | 'stopping'
  | 'error';

/**
 * Translate a backend `ChannelState` to the mockup's `MockupChannelStatus`.
 * Covers all six backend states plus `undefined` (loading / legacy rows).
 */
export function toMockupStatus(
  backendStatus: ChannelState | undefined,
): MockupChannelStatus {
  switch (backendStatus) {
    case 'running':
      return 'live';
    case 'ad_break':
      return 'adbreak';
    case 'starting':
      return 'starting';
    case 'stopping':
      return 'stopping';
    case 'error':
      return 'error';
    case 'idle':
    case undefined:
    default:
      return 'idle';
  }
}

/**
 * Translate a backend `outputDestination` enum into the mockup's display
 * array. The mockup renders destinations as a comma-separated list; the
 * backend stores the union literal.
 */
export function toDestinationsArray(
  dest: 'youtube_live' | 'o_and_o' | 'both' | undefined,
): readonly string[] {
  switch (dest) {
    case 'both':
      return ['YouTube', 'Owned-and-operated player'];
    case 'youtube_live':
      return ['YouTube'];
    case 'o_and_o':
      return ['Owned-and-operated player'];
    default:
      return [];
  }
}

/**
 * DISCREPANCY-2 microfix (architecture-plan §1.3).
 *
 * The mockup's `EmailPillInput` produces a `string[]`; the live
 * `api.channels.updateChannel` validator's `alertEmailRecipients` field
 * accepts either `string` (CSV) or `string[]` depending on the deployed
 * backend version. This adapter normalises BOTH inbound shapes so the
 * component can call it unconditionally; T321 narrows the live type via
 * `tsc -b` and the dead branch remains as defence-in-depth against future
 * backend schema changes.
 *
 * Behaviour:
 *   - `string[]` input → CSV-joined `string` output (array branch)
 *   - `string`   input → pass-through `string` output (string branch)
 */
export function alertEmailRecipientsAdapter(
  rawValue: string | readonly string[],
): string {
  return Array.isArray(rawValue) ? rawValue.join(',') : (rawValue as string);
}

// Dev-mode exposure for smoke-suite assertions. Gated by `import.meta.env.DEV`
// so the production bundle drops the block entirely. The additional `window`
// guard prevents a `ReferenceError` when this module loads under vitest's
// default node environment (no DOM).
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__toMockupStatus =
    toMockupStatus;
  (window as unknown as Record<string, unknown>).__toDestinationsArray =
    toDestinationsArray;
}
